/**
 * Zod validation type mappings for schema fields and form fields
 */

import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import {
  assertSupportedFormFieldTree,
  assertSupportedSchemaFieldTree,
  assertSupportedSchemaFieldType
} from '@core-engine/schema/type-mappers/validation.js'
import type { FormField, SchemaField } from '@core-engine/schema/types.js'
import { quotePropertyName } from '@core-engine/utils/string.js'

// ============================================================================
// Schema Field → Zod Type (for form validation)
// ============================================================================

/**
 * Map a schema field type to Zod validation schema
 * @param field - The schema field
 * @returns The Zod schema expression as a string
 */
export function toZodType(field: SchemaField): string {
  assertSupportedSchemaFieldTree(field)

  const label = field.label || field.name

  switch (field.type) {
    case 'uuid':
    case 'serial':
      return field.required
        ? `z.string().uuid('${label} must be a valid UUID')`
        : `z.string().uuid('${label} must be a valid UUID').optional()`
    case 'number':
    case 'decimal':
      return field.required
        ? `z.number({ message: '${label} is required' })`
        : 'z.number().optional()'
    case 'boolean':
      return 'z.boolean()'
    case 'email': {
      const base = field.required
        ? `z.string().min(1, '${label} is required').email('Please enter a valid email address')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || z.string().email().safeParse(val).success, { message: 'Please enter a valid email address' })`
      return field.length ? `${base}.max(${field.length})` : base
    }
    case 'string':
    case 'varchar':
    case 'text':
    case 'markdown':
    case 'richtext': {
      if (field.name.toLowerCase().includes('email')) {
        const base = field.required
          ? `z.string().min(1, '${label} is required').email('Please enter a valid email address')`
          : `z.string().email('Please enter a valid email address').optional()`
        return field.length ? `${base}.max(${field.length})` : base
      }
      const base = field.required ? `z.string().min(1, '${label} is required')` : 'z.string()'
      return field.length ? `${base}.max(${field.length})` : base
    }
    case 'date':
      if (!field.required) {
        return 'z.string().transform(val => val === "" ? undefined : val).optional()'
      }
      return `z.string().min(1, '${label} is required')`
    case 'timestamp':
    case 'time':
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'image':
    case 'video':
    case 'media': {
      if (!field.required) {
        return 'z.string().transform(val => val === "" ? undefined : val).optional()'
      }
      const mediaType =
        field.type === 'image' ? 'an image' : field.type === 'video' ? 'a video' : 'media'
      return `z.string().min(1, 'Please select ${mediaType} for ${label}')`
    }
    case 'gallery':
      return field.required
        ? `z.array(z.string()).min(1, '${label} must have at least one item')`
        : 'z.array(z.string()).optional()'
    case 'list':
      return buildZodListType(field, label)
    case 'select': {
      if (field.multiple) {
        if (field.creatable) {
          return field.required
            ? `z.array(z.string()).min(1, '${label} is required')`
            : 'z.array(z.string()).optional()'
        }
        if (field.options && field.options.length > 0) {
          const values = field.options.map((opt) => `'${opt.value}'`).join(', ')
          const valuesArray = `[${values}] as const`
          const arraySchema = `z.array(z.string()).refine((values) => values.every((value) => (${valuesArray} as readonly string[]).includes(value)), { message: 'Invalid ${label}' })`
          return field.required
            ? `${arraySchema}.min(1, '${label} is required')`
            : `${arraySchema}.optional()`
        }
        return field.required
          ? `z.array(z.string()).min(1, '${label} is required')`
          : 'z.array(z.string()).optional()'
      }
      if (field.options && field.options.length > 0) {
        const values = field.options.map((opt) => `'${opt.value}'`).join(', ')
        const valuesArray = `[${values}] as const`
        return field.required
          ? `z.string().min(1, '${label} is required').refine((val) => (${valuesArray} as readonly string[]).includes(val), { message: 'Invalid ${label}' })`
          : `z.string().refine((val) => !val || (${valuesArray} as readonly string[]).includes(val), { message: 'Invalid ${label}' }).optional()`
      }
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    }
    case 'icon':
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'relationship': {
      if (field.multiple) {
        if (isUserRelationshipTarget(field.relationship)) {
          return field.required
            ? `z.array(z.string()).min(1, '${label} is required')`
            : 'z.array(z.string()).optional()'
        }
        return field.required
          ? `z.array(z.string().uuid()).min(1, '${label} is required')`
          : 'z.array(z.string().uuid()).optional()'
      }
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    }
    default:
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
  }
}

/**
 * Build Zod type for list fields
 */
function buildZodListType(field: SchemaField, label: string): string {
  if (field.items) {
    assertSupportedSchemaFieldType(field.items as SchemaField)
  }

  if (field.fields && field.fields.length > 0) {
    const nestedFields = field.fields
      .flatMap((nestedField) => {
        const defs: string[] = []
        const nestedZodType = toZodType(nestedField)
        const alreadyOptional = nestedZodType.includes('.optional()')
        let nestedDef = `    ${quotePropertyName(nestedField.name)}: ${nestedZodType}`
        if (!nestedField.required && !alreadyOptional) {
          nestedDef += '.optional()'
        }
        defs.push(nestedDef)
        if (nestedField.hasIcon) {
          defs.push(`    ${quotePropertyName(`${nestedField.name}Icon`)}: z.string().optional()`)
        }
        return defs
      })
      .join(',\n')
    const objectSchema = `z.object({\n${nestedFields}\n  })`
    const arraySchema = field.maxItems
      ? `z.array(${objectSchema}).max(${field.maxItems}, '${label} cannot exceed ${field.maxItems} items')`
      : `z.array(${objectSchema})`
    return field.required
      ? `${arraySchema}.min(1, '${label} must have at least one item')`
      : `${arraySchema}.optional()`
  }
  if (
    field.items?.type === 'string' ||
    field.items?.type === 'email' ||
    field.items?.type === 'varchar'
  ) {
    const itemSchema = field.items.length ? `z.string().max(${field.items.length})` : 'z.string()'
    const arraySchema = field.maxItems
      ? `z.array(${itemSchema}).max(${field.maxItems}, '${label} cannot exceed ${field.maxItems} items')`
      : `z.array(${itemSchema})`
    return field.required
      ? `${arraySchema}.min(1, '${label} must have at least one item')`
      : `${arraySchema}.optional()`
  }
  return field.required
    ? `z.array(z.string()).min(1, '${label} must have at least one item')`
    : 'z.array(z.string()).optional()'
}

// ============================================================================
// Form Field → Zod Type (for form-pipeline)
// ============================================================================

export interface FormFieldZodOptions {
  treatAsOptional?: boolean
}

/**
 * Map a form field type to Zod validation schema
 * @param field - The form field
 * @param options - Options for generating the zod type
 * @returns The Zod schema expression as a string
 */
export function formFieldToZodType(field: FormField, options: FormFieldZodOptions = {}): string {
  assertSupportedFormFieldTree(field)

  const label = field.label || field.name || ''
  const isRequired = field.required && !options.treatAsOptional

  switch (field.type) {
    case 'text': {
      let zodType = isRequired ? `z.string().min(1, '${label} is required')` : 'z.string()'
      if (field.minLength) {
        zodType += `.min(${field.minLength}, '${label} must be at least ${field.minLength} characters')`
      }
      if (field.maxLength) {
        zodType += `.max(${field.maxLength}, '${label} must be at most ${field.maxLength} characters')`
      }
      if (field.pattern) {
        zodType += `.regex(new RegExp('${field.pattern}'), 'Invalid ${label} format')`
      }
      return isRequired ? zodType : `${zodType}.optional()`
    }
    case 'textarea': {
      let zodType = isRequired ? `z.string().min(1, '${label} is required')` : 'z.string()'
      if (field.minLength) {
        zodType += `.min(${field.minLength}, '${label} must be at least ${field.minLength} characters')`
      }
      if (field.maxLength) {
        zodType += `.max(${field.maxLength}, '${label} must be at most ${field.maxLength} characters')`
      }
      return isRequired ? zodType : `${zodType}.optional()`
    }
    case 'email':
      return isRequired
        ? `z.string().min(1, '${label} is required').email('Please enter a valid email address')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || z.string().email().safeParse(val).success, { message: 'Please enter a valid email address' })`
    case 'phone': {
      const pattern =
        field.pattern || '^[+]?[(]?[0-9]{1,4}[)]?[-\\s\\.]?[(]?[0-9]{1,4}[)]?[-\\s\\.]?[0-9]{1,9}$'
      return isRequired
        ? `z.string().min(1, '${label} is required').regex(new RegExp('${pattern}'), 'Please enter a valid phone number')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || new RegExp('${pattern}').test(val), { message: 'Please enter a valid phone number' })`
    }
    case 'number': {
      let zodType = isRequired ? `z.number({ message: '${label} is required' })` : 'z.number()'
      if (field.min !== undefined) {
        zodType += `.min(${field.min}, '${label} must be at least ${field.min}')`
      }
      if (field.max !== undefined) {
        zodType += `.max(${field.max}, '${label} must be at most ${field.max}')`
      }
      return isRequired ? zodType : `${zodType}.optional()`
    }
    case 'url':
      return isRequired
        ? `z.string().min(1, '${label} is required').url('Please enter a valid URL')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || z.string().url().safeParse(val).success, { message: 'Please enter a valid URL' })`
    case 'date':
      return isRequired
        ? `z.string().min(1, '${label} is required')`
        : 'z.string().transform(val => val === "" ? undefined : val).optional()'
    case 'select':
    case 'radio':
      if (field.options && field.options.length > 0) {
        const values = field.options.map((opt) => `'${opt.value}'`).join(', ')
        return isRequired
          ? `z.enum([${values}], { message: '${label} is required' })`
          : `z.enum([${values}]).optional()`
      }
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'checkbox':
      return isRequired
        ? `z.boolean().refine(val => val === true, { message: '${label} is required' })`
        : 'z.boolean().optional()'
    case 'multiselect':
      return isRequired
        ? `z.array(z.string()).min(1, '${label} is required')`
        : 'z.array(z.string()).optional()'
    case 'timezone':
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'list':
      if (field.fields && field.fields.length > 0) {
        const nestedFields = field.fields
          .map((f) => `${f.name}: ${formFieldToZodType(f, options)}`)
          .join(', ')
        return isRequired
          ? `z.array(z.object({ ${nestedFields} })).min(1, '${label} is required')`
          : `z.array(z.object({ ${nestedFields} })).optional()`
      }
      return isRequired
        ? `z.array(z.string()).min(1, '${label} is required')`
        : 'z.array(z.string()).optional()'
    case 'file':
    case 'upload':
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    default:
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
  }
}
